import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { addMonths, beginningOfToday, formatIsoDate } from 'lib/datetime/dateUtils'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import AccommodationCalendarDayPicker, { AccommodationDayView } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarDayPicker'
import { AccommodationMobileDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarSmallDayCell'
import { fillArray } from 'lib/array/arrayUtils'
import AccommodationCalendarMonthPicker, { AccommodationCalendarYearSelectHandler, AccommodationMonthView } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarMonthPicker'
import { AccommodationDayPickerSize } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarDayMonth'
import noop from 'lib/function/noop'

const now = beginningOfToday()

/**
 * This date picker isn't run from service calendar data so we don't have a time period of which dates to show
 * Lets a fairly large selection of time - 3 years (36 months) worth of days/month to pick from
 */
const monthViews: Array<AccommodationMonthView> = fillArray(36).map((index): AccommodationMonthView => {
  const month = addMonths(now, index)
  return {
    month: month.getMonth(),
    year: month.getFullYear(),
    durationUnit: 'night',
    soldOut: false,
  }
})

const dayViews: Array<AccommodationDayView> = fillArray(36).flatMap((monthIndex) => {
  const month = addMonths(now, monthIndex)
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  // If we're handling the current month, include the current day as part of the tally.
  const daysLeftInMonth = monthIndex === 0 ? daysInMonth - month.getDate() + 1 : daysInMonth

  return fillArray(
    daysLeftInMonth,
    // `dayIndex` is zero-indexed, and we're transforming it to be one-indexed. If we're handling
    // the current month, we need to include the current day.
    dayIndex => monthIndex === 0 ? dayIndex + month.getDate() : dayIndex + 1,
  ).map((day): AccommodationDayView => {
    const date = new Date(month.getFullYear(), month.getMonth(), day)
    return {
      date: formatIsoDate(date),
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear(),
      durationUnit: 'night',
      priceType: 'default',
      soldOut: false,
    }
  })
})

interface Props {
  checkInDate?: string
  checkOutDate?: string
  /**
   * Reference to the scroll container element this picker sits in
   * This is necessary to provide so it can manage showing the correct month
   */
  scrollerRef: React.RefObject<HTMLElement | null>
  size: AccommodationDayPickerSize
  /**
   * The month we're picking the date for inso ISO date format
   */
  selectedMonth?: string
  /**
   * Event for when the date picker wants to go back to the month picker
   */
  onBackToMonth?: () => void
  /**
   * What happens when the user selects a month from the mont view
   */
  onMonthSelect?: AccommodationCalendarYearSelectHandler

  variant?: 'both' | 'day-only'

  /**
   * Event for when the selected dates change
   */
  onDatesChange?: AccommodationMobileDayCellSelectHandler
}

function OfferDatePicker({
  checkInDate,
  scrollerRef,
  checkOutDate,
  size,
  selectedMonth,
  onMonthSelect,
  onBackToMonth,
  variant = 'both',
  onDatesChange = noop,
}: Props) {
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDatesSelect = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    onDatesChange(day)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.DateChanged)
  }, [onDatesChange, onUserAnalyticsAction])

  const month = useMemo(() => selectedMonth ? new Date(selectedMonth) : undefined, [selectedMonth])

  useEffect(() => {
    if (!month) {
      scrollerRef.current?.scrollTo({ behavior: 'auto', top: 0 })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  if (variant === 'day-only') {
    return <AccommodationCalendarDayPicker
      startDate={checkInDate}
      endDate={checkOutDate}
      onDaySelect={onDatesSelect}
      contentRef={scrollerRef}
      views={dayViews}
      onBackToMonth={onBackToMonth}
      initialMonth={month?.getMonth()}
      initialYear={month?.getFullYear()}
      size={size}
    />
  }

  return <>
    {month && <AccommodationCalendarDayPicker
      startDate={checkInDate}
      endDate={checkOutDate}
      onDaySelect={onDatesChange}
      contentRef={scrollerRef}
      views={dayViews}
      onBackToMonth={onBackToMonth}
      initialMonth={month.getMonth()}
      initialYear={month.getFullYear()}
      size={size}
    />}
    {!month && <AccommodationCalendarMonthPicker
      views={monthViews}
      onSelect={onMonthSelect}
    />}
  </>
}

export default withOfferPageState(state => ({
  open: !!state.activeFilter || state.activeHeaderFilter === 'checkin',
}), OfferDatePicker, preventRenderWithClosedFilter)
